import React, { FC } from 'react';
import { Box } from '@mui/material';
import { useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import { ISinglePro } from './state';
import { ExpertIdPill } from '../ExpertIdPill/ExpertIdPill';
import { Breadcrumbs, Crumb } from '../Breadcrumbs';
import { Button } from '../Button/Button';
import { IconShare } from '../../utils/icons';
import { isInternal } from '../../utils';
import { useUser } from '../../hooks/useUser';
import { usePortalPath } from '../../hooks/usePortalPath';

interface Props {
  expert?: ISinglePro;
  isSharePage?: boolean;
}

/**
 * Breadcrumb row matching the portal convention (project details / video
 * details pages): Breadcrumbs left, actions right, px-[24px], no bar.
 */
const ProfileHeaderBar: FC<Props> = ({ expert, isSharePage }) => {
  const location = useLocation();
  const { role } = useUser();
  const { p } = usePortalPath();

  // Brand users browse the listing on this same path (/experts or
  // /experts/featured) — linking back to it drops proId and restores the
  // listing in place. Everyone else goes to /experts.
  //
  // Compared with endsWith rather than === because the path is slugged for a
  // brand client (/portal/acme/experts/featured) since TDC-1609; p() then puts
  // the prefix back on the crumb. The share page keeps its own root-absolute
  // listing (PortalScope already forces basePath to '' there, but being
  // explicit keeps it obvious).
  let listingPath = p('/experts');
  if (isSharePage) listingPath = '/experts/share';
  else if (location.pathname.endsWith('/experts/featured')) listingPath = p('/experts/featured');

  const crumbs: Crumb[] = [
    { label: 'Experts', to: listingPath },
    { label: expert?.displayName || 'Expert' },
  ];

  // Parity with production's three share affordances (ListProCard.tsx,
  // ProCard.js and createEditProfile/ExpertFormMenu.tsx): all gate on
  // `status === 'Active'`, and neither exists in the brand portal. The link is
  // public and unauthenticated, so an ungated one would let a brand publish a
  // profile for an expert who is not live.
  const canShare = isInternal(role) && expert?.status === 'Active';
  const shareUrl = canShare && expert?.userid
    ? `${window.location.origin}/experts/share?proId=${expert.userid}`
    : '';

  const copyShareUrl = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success('Share link copied');
    } catch {
      toast.error('Could not copy link');
    }
  };

  return (
    <Box className="flex items-start justify-between w-full flex-wrap gap-2 px-[24px]">
      <Box className="min-w-0">
        <Breadcrumbs items={crumbs} />
      </Box>
      {!isSharePage && (
        <Box className="flex items-center gap-2 shrink-0">
          <ExpertIdPill userid={expert?.userid} />
          {!!shareUrl && (
            <Button
              variant="soft"
              color="default"
              icon={<IconShare size={18} stroke="currentColor" />}
              onClick={copyShareUrl}
              className="!min-w-0 !h-10 gap-2"
            >
              Share
            </Button>
          )}
        </Box>
      )}
    </Box>
  );
};

export default ProfileHeaderBar;
